import './styles/singlePage.scss';

import React, { useMemo, useState } from 'react';

import { AddToCartButtonContainer } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart';
import { ApplianceImage } from '../components/appliance';
import AvailabilityComponent from '../components/Primitives/AvailabilityComponent';
import { Breadcrumb } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import CategoryArchiveComponent from '../components/Primitives/CategoryArchiveComponent';
import { GetDocDisplayName } from '../utils';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import downloadIconSvg from '@progymedia/sbi-web-englander/src/images/englander-icons/Download-icon.svg';
import { graphql } from 'gatsby';
import informationIcon from '@progymedia/sbi-web-englander/src/images/englander-icons/Information-Icon.svg';
import marked from 'marked';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useTranslation } from 'react-i18next';
import { Metadata } from '../models/metadata';

export const query = graphql`
    query SinglePage($code: String!) {
        single: productsJson(code: { eq: $code }) {
            id
            code
            slug {
                en_CA
                en_US
                fr_CA
            }
            category
            title {
                en_CA
                en_US
                fr_CA
            }
            documents {
                en_CA
                en_US
                fr_CA
            }
            images {
                en_CA
                en_US
                fr_CA
            }
            fields {
                ...productsFieldsImagesFragment
            }
            attributes {
                video_ids {
                    values {
                        fr_CA
                        en_US
                        en_CA
                    }
                }
            }
            category_localized {
                en_CA
                en_US
                fr_CA
            }
            description {
                en_CA
                en_US
                fr_CA
                valuesMarkdownRemark {
                    en_CA
                    en_US
                    fr_CA
                }
            }
        }
    }
`;

export default function SinglePage({
    data,
    pageContext,
}: {
    data: Queries.SinglePageQuery;
    pageContext: any;
}) {
    const single = data.single;
    const { t } = useTranslation('pages');
    const { currentLocale } = usePageLocalization();
    const document = single?.documents?.[0];
    const [price] = useProductPrice(single?.code ?? undefined);
    marked.setOptions({
        breaks: true,
        renderer: new marked.Renderer(),
        gfm: true,
    });
    const slug = single?.slug?.['en_CA']?.replace('/englander', '');
    const category = slug?.split('/')[1];
    const ventingCategory = slug?.split('/')[2];
    const subCategory = slug?.split('/')[3];
    const singleCategories =
        single?.category_localized[currentLocale].split('/');
    const type = slug?.split('/')[2];

    const [qtyValue, setQtyValue] = useState(1);

    const categoryArchivesCms = pageContext?.cmsData?.nodes?.[0];
    const captions = categoryArchivesCms?.images
        ?.filter((d) => d?.caption?.en_CA != 'Pellet Stoves')
        .map((e: any, index: number) => {
            return { ...e, index };
        });

    const getMetadata = (category: string, single: any): Metadata => {
        const metadata = {
            metaTitle: '',
            metaDescription: '',
        };

        const sku = single?.code as string;
        const productTitle = single?.title?.[currentLocale] as string;
        const seoSubCategory =
            single?.category_localized?.[currentLocale]?.split('/')[3];

        if (category === 'parts') {
            metadata.metaTitle = t('metadata:part.title', { SKU: sku });
            metadata.metaDescription = t('metadata:part.description', {
                TITLE: productTitle,
                SKU: sku,
            });
        } else if (category === 'accessories' || category === 'venting') {
            metadata.metaTitle = t('metadata:single.title', {
                SKU: sku,
                CATEGORY: seoSubCategory,
            });
            metadata.metaDescription = t('metadata:single.description', {
                TITLE: productTitle,
                SKU: sku,
            });
        } else {
            metadata.metaTitle = '';
            metadata.metaDescription = '';
        }

        return metadata;
    };

    const metadata = useMemo<Metadata>(
        () => getMetadata(category ?? '', single),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentLocale, category]
    );

    return (
        <Layout>
            <SEO
                title={metadata.metaTitle}
                description={metadata.metaDescription}
            />
            <div className='container'>
                <div className='single-page'>
                    <Breadcrumb
                        className='single-listing-breadcrumb'
                        elements={[
                            { link: '/', name: t('layout:appliance.home') },
                            {
                                link: `/${category}/`,
                                name: singleCategories[2],
                            },
                            {
                                link:
                                    t(`url:${category}`) +
                                    '/' +
                                    t(`url:${type}`) +
                                    '/',
                                name: singleCategories[3],
                            },
                            { link: '', name: single.code },
                        ]}
                    />
                    <div className='single-infos'>
                        {single.images && (
                            <div className='left'>
                                <ApplianceImage
                                    appliance={single}
                                    currentLocale={currentLocale}
                                />
                            </div>
                        )}
                        <div className='right'>
                            <h1 className='single-title'>
                                <div className='code'>{single?.code}</div>
                                <div>
                                    {single?.title?.[currentLocale] ?? 'null'}
                                </div>
                            </h1>
                            {/* <div className='reviews'>
                                TODO REVIEWS // TODO verifier si sbi veut vraiment le review dans la page des single ou si c'est un copy/paste de la page d'appareil.
                            </div> */}

                            <div className='price-section'>
                                {getProductPrice(price, currentLocale)}
                            </div>

                            <AvailabilityComponent product={single} />

                            <AddToCartButtonContainer
                                product={single}
                                qtyValue={qtyValue}
                                setQtyValue={setQtyValue}
                            />
                            {single?.description?.valuesMarkdownRemark?.[
                                currentLocale
                            ] && (
                                <div
                                    className='description'
                                    dangerouslySetInnerHTML={{
                                        __html: single.description
                                            .valuesMarkdownRemark[
                                            currentLocale
                                        ],
                                    }}
                                />
                            )}
                            {document && (
                                <div className='actions'>
                                    <a
                                        rel='noreferrer'
                                        target='_blank'
                                        href={
                                            '/file/' + document[currentLocale]
                                        }
                                        className='download-text'
                                    >
                                        <p className='filename'>
                                            {GetDocDisplayName(
                                                document,
                                                single?.code
                                            )}
                                            <img src={downloadIconSvg} alt='' />
                                        </p>
                                    </a>
                                </div>
                            )}

                            <div className='contact-us'>
                                <img src={informationIcon} alt='' />
                                <span>{t('venting.need-assistance')}</span>
                                <a href='mailto:info@englander-stoves.com?'>
                                    {' '}
                                    {/* //TODO Valider email et call to action */}
                                    <span className='contact-txt'>
                                        {t('venting.contact-us')}
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                    {categoryArchivesCms?.fields?.images?.length && (
                        <CategoryArchiveComponent
                            title={t('venting.continue-shopping', {
                                category: t(`url:${category}`),
                            })}
                            categoryNumber={4}
                            images={categoryArchivesCms?.fields?.images}
                            captions={captions.filter(
                                (d: any) => d?.caption?.en_CA != 'Pellet Stoves'
                            )}
                            category={category}
                            sortedAplhabetically={false}
                            subCategory={subCategory}
                            ventingCategory={ventingCategory}
                        />
                    )}
                </div>
            </div>
        </Layout>
    );
}

function getProductPrice(price, currentLocale) {
    if (price?.regularPrice.value === 0) {
        return '--';
    }
    if (price?.regularPrice.value === price?.finalPrice.value) {
        return (
            <span className='final-price'>
                {LocalePrice(currentLocale, price?.regularPrice.value)}
            </span>
        );
    } else if (
        price?.regularPrice.value &&
        price?.finalPrice.value &&
        price.regularPrice.value !== price.finalPrice.value
    ) {
        return (
            <>
                <span className='regular-price'>
                    {LocalePrice(currentLocale, price?.regularPrice.value)}
                </span>
                <span className='final-price'>
                    {LocalePrice(currentLocale, price?.finalPrice.value)}
                </span>
            </>
        );
    }
}
